import { DecoratorPermissionsType, DecoratorType, Layout, PermDefinitionConstants } from "@mcleod/components";
import { PermsChangedCallback, SecureItem } from "./SecureItem";
import { RowPermsSecureItem } from "../models/ModelPermsSecureItem";

/**
 * This class generates the SecureItem objects for the root layout.
 */
export class RootLayoutSecureItems {
    private layout: Layout;
    private path: string;
    private permsChanged: PermsChangedCallback;

    constructor(layout: Layout, path: string, permsChanged: PermsChangedCallback) {
        this.layout = layout;
        this.path = path;
        this.permsChanged = permsChanged;
    }

    get secureItems(): SecureItem[] {
        return [
            this.entireScreenViewSecureItem,
            this.entireScreenEditSecureItem,
            this.addDecoratorSecureItem,
            this.updateDecoratorSecureItem,
            this.searchDecoratorSecureItem
        ].filter(value => value != null);
    }

    private get entireScreenViewSecureItem(): SecureItem {
        return new SecureItem({
            pageIdentifier: this.path,
            itemName: RowPermsSecureItem.ENTIRE_SCREEN,
            permsDef: PermDefinitionConstants.ENTIRE_SCREEN_VIEW,
            permsChanged: this.permsChanged
        });
    }

    private get entireScreenEditSecureItem(): SecureItem {
        return new SecureItem({
            pageIdentifier: this.path,
            itemName: RowPermsSecureItem.ENTIRE_SCREEN,
            permsDef: PermDefinitionConstants.ENTIRE_SCREEN_EDIT,
            permsChanged: this.permsChanged
        });
    }

    private get addDecoratorSecureItem(): SecureItem {
        if (!this.layout.getAllowedUses().includes(DecoratorType.ADD)) {
            return null;
        }
        return new SecureItem({
            pageIdentifier: this.path,
            itemName: DecoratorPermissionsType.ADD,
            permsDef: {
                permsType: DecoratorPermissionsType.ADD,
                description: "Add security",
                availableToAllDescription: "Everyone can use this layout to add a new record",
                availableToNoneDescription: "No one can use this layout to add a new record"
            },
            permsChanged: this.permsChanged
        });
    }

    private get updateDecoratorSecureItem(): SecureItem {
        if (!this.layout.getAllowedUses().includes(DecoratorType.UPDATE)) {
            return null;
        }
        return new SecureItem({
            pageIdentifier: this.path,
            itemName: DecoratorPermissionsType.UPDATE,
            permsDef: {
                permsType: DecoratorPermissionsType.UPDATE,
                description: "Update security",
                availableToAllDescription: "Everyone can use this layout to update a record",
                availableToNoneDescription: "No one can use this layout to update a record"
            },
            permsChanged: this.permsChanged
        });
    }

    private get searchDecoratorSecureItem(): SecureItem {
        if (!this.layout.getAllowedUses().includes(DecoratorType.SEARCH)) {
            return null;
        }
        return new SecureItem({
            pageIdentifier: this.path,
            itemName: DecoratorPermissionsType.SEARCH,
            permsDef: {
                permsType: DecoratorPermissionsType.SEARCH,
                description: "Advanced Search security",
                availableToAllDescription: "Everyone can use this layout for advanced searching",
                availableToNoneDescription: "No one can use this layout for advanced searching",
            },
            permsChanged: this.permsChanged
        });
    }
}
